import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Title from "../../atoms/Title/Title";
import LogoSvg from "../../atoms/LogoSvg/LogoSvg";
import INFO from "../../../assets/images/INFO.svg";
import SubscriptionCard from "../../molecules/SubscriptionCard/SubscriptionCard";
import ModalSubscriptionInformations from "../../molecules/ModalSubscriptionInformations/ModalSubscriptionInformations";
import { OffersState } from "../../../types/rtk/subscription/subscription";
import { RootState } from "../../../types/rtk/api-config/types";
import { useAppDispatch, useAppSelector } from "../../../rtk/_config/hooks";
import Text from "../../atoms/Text/Text";
import { setBasket } from "../../../rtk/basket/basket";
import Carousel from '../../molecules/Carousel/Carousel';
import SubscriptionCarouselCard from '../../molecules/SubscriptionCarouselCard/SubscriptionCarouselCard';
import SeeMoreCarouselCard from '../../molecules/SeeMoreCarouselCard/SeeMoreCarouselCard';

interface Props {
  datas: OffersState;
  handleOpenGiftCard: () => void;
}

const SubscriptionChoice = ({ datas, handleOpenGiftCard }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const basket = useAppSelector((state: RootState) => state.basket);
  const giftcardSelected: boolean = !!basket.giftcard;

  // Needed for the A-B testing. Remove when not needed anymore.
  const userId = useAppSelector((state: RootState) => state.tracker.user_id);
  const isCarouselVariant = userId % 2 === 1;

  const [isModalOpenInformation, setIsModalOpenInformation] = useState(false);
  const [showMoreOffers, setShowMoreOffers] = useState(false);

  const handleOpenInformation = () => setIsModalOpenInformation(true);
  const handleCloseInformation = () => setIsModalOpenInformation(false);

  const allOffers = datas.subscriptionsData;

  // Preselect offer with period=36 on mount if no offer is selected.
  // The carousel handles its own selection, where no offer is a valid state (on the "see more" card).
  useEffect(() => {
    if (!isCarouselVariant && !basket.offer_id && allOffers.length > 0) {
      const defaultOffer = allOffers.find(offer => offer.period === 36);
      if (defaultOffer) {
        dispatch(setBasket({ ...basket, offer_id: defaultOffer.id }));
      }
    }
  }, [allOffers, basket, dispatch, isCarouselVariant]);

  // Filter offers to hide certain periods unless selected or "show more" is active
  const hiddenOffersPeriods = [1];
  const visibleOffers = allOffers.filter(
    (item) =>
      item.id === basket.offer_id ||
      !hiddenOffersPeriods.includes(item.period) ||
      showMoreOffers
  ).sort((a, b) => a.period - b.period);
  const hasHiddenOffers = visibleOffers.length !== allOffers.length;

  const carouselOffers = useMemo(() => visibleOffers.sort((a, b) => b.period - a.period), [visibleOffers])
  const [selectedIndex, updateIndex] = useState<number | null>(carouselOffers.findIndex((offer) => offer.period === 24));

  // The centered card is the selected offer, the "see more" card selects none.
  // Only dispatched on carousel moves, so basket updates (e.g. a gift card) are never overwritten.
  const selectCarouselItem = (index: number) => {
    updateIndex(index);
    dispatch(setBasket({ ...basket, offer_id: carouselOffers[index]?.id ?? null }));
  };

  // Syncs the basket once with the card centered on arrival
  const isCarouselInitialized = useRef(false);
  useEffect(() => {
    if (!isCarouselVariant || isCarouselInitialized.current || selectedIndex === null) return;
    isCarouselInitialized.current = true;
    dispatch(setBasket({ ...basket, offer_id: carouselOffers[selectedIndex]?.id ?? null }));
  }, [basket, carouselOffers, dispatch, isCarouselVariant, selectedIndex]);

  return (
    <Box className="SubscriptionChoice">
      <Box className="subscriptionSubtitleContainer">
        <Title
          variant="h1"
          className="subscriptionSubtitle"
          children={t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.TITLE")}
          style={isCarouselVariant ? { padding: '1rem' } : undefined}
        />
        <Box className="subscriptionInfoLogo">
          <LogoSvg src={INFO} onClick={handleOpenInformation} />
        </Box>
      </Box>

      {
        isCarouselVariant
        ? (
          <Carousel
            onChange={selectCarouselItem}
            initialIndex={selectedIndex ?? 0}
            items={
              [
                ...carouselOffers
                  .map((obj, i) => {
                    return (
                      <SubscriptionCarouselCard
                        key={obj.id}
                        data={obj}
                        isActive={i === selectedIndex}
                      />
                    );
                  }),
                ...hasHiddenOffers 
                  ? [<SeeMoreCarouselCard key="see-more" onClick={() => setShowMoreOffers((v) => !v)} />] 
                  : []
              ]
            }
            showArrows={false}
          />
        )
      : (
        <Box>
          {visibleOffers.map((obj, i) => {
            return (
              <SubscriptionCard
                key={i}
                index={i}
                data={obj}
                isActive={!giftcardSelected && basket.offer_id === obj.id}
                offers={allOffers}
              />
            );
          })}
          <SubscriptionCard
            isActive={giftcardSelected}
            onSelect={handleOpenGiftCard}
            offers={allOffers}
          />
          {hasHiddenOffers && (
            <Box className="toggleOffer" onClick={() => setShowMoreOffers((v) => !v)}>
              <Text children={t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.SEE_MORE")} />
            </Box>
          )}
        </Box>
        )
      }

      <ModalSubscriptionInformations
        isModalOpen={isModalOpenInformation}
        handleClose={handleCloseInformation}
      />
    </Box>
  );
};

export default SubscriptionChoice;
